import { Modal, TabPaneProps, Tabs } from "antd";
import { useDispatch, useSelector } from "app/store";
import ListPageLayout from "components/layout/ListPageLayout";
import TabsWrapper from "components/ui/TabsWrapper";
import Claims from "features/auth/claims";
import useAuth from "features/auth/useAuth";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import GeneralSettings from "./GeneralSettings";
import styles from "./Settings.module.scss";
import StatusesList from "./statuses/StatusesList";
import StatusChangeRules from "./statuses/StatusChangeRules";

interface CustomTabPaneProps extends Omit<TabPaneProps, "key"> {
  key: string;
}

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams("");
  const activeTabParam = searchParams.get("tab");
  const { hasClaim } = useAuth();
  const { isSaveDisabled } = useSelector((state) => state.statuses);

  const tabs = useMemo<CustomTabPaneProps[]>(
    () => [
      ...(hasClaim([Claims.Setting.Read])
        ? ([
            {
              tab: t("settings.tabs.generalSettings"),
              key: "generalSetting",
              children: <GeneralSettings />,
            },
          ] as CustomTabPaneProps[])
        : []),

      ...(hasClaim([Claims.FailureStatus.Read])
        ? [
            {
              tab: t("settings.tabs.statuses"),
              key: "statuses",
              children: <StatusesList />,
            },
            {
              tab: t("settings.tabs.statusesChangeRules"),
              key: "statusesChangeRules",
              children: <StatusChangeRules />,
            },
          ]
        : []),
    ],
    [t, hasClaim]
  );

  const defaultActiveKey = useMemo(() => {
    const activeTab = tabs.find((tab) => tab.key === activeTabParam);

    return activeTab ? activeTab.key : tabs[0].key;
  }, [activeTabParam, tabs]);

  const handleTabsChange = useCallback(
    (activeKey: string) => {
      if (isSaveDisabled) {
        setSearchParams(new URLSearchParams(`tab=${activeKey}`));
      } else {
        Modal.confirm({
          title: t("common.navigationBlocker.title"),
          content: t("common.navigationBlocker.content"),
          okText: t("common.navigationBlocker.confirm"),
          cancelText: t("common.navigationBlocker.stayOnPage"),
          onOk: () => {
            dispatch.statuses.setIsSaveDisabled(true);
            setSearchParams(new URLSearchParams(`tab=${activeKey}`));
          },
          centered: true,
        });
      }
    },
    [setSearchParams, isSaveDisabled, t, dispatch.statuses]
  );

  useEffect(() => {
    return () => {
      dispatch.settings.reset();
      dispatch.statuses.reset();
    };
  }, [dispatch.settings, dispatch.statuses]);

  return (
    <ListPageLayout title={t("menu.settings")}>
      <TabsWrapper>
        <Tabs
          activeKey={defaultActiveKey}
          destroyInactiveTabPane
          onChange={handleTabsChange}
          type="card"
          size="small"
        >
          {tabs.map(({ tab, key, children }) => (
            <Tabs.TabPane tab={tab} key={key} className={styles.tab}>
              {children}
            </Tabs.TabPane>
          ))}
        </Tabs>
      </TabsWrapper>
    </ListPageLayout>
  );
};

export default Settings;
